import { useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useCreateDocumentRequest } from '@/hooks/use-document-requests'
import { getApiErrorMessage } from '@/lib/api'

export function CreateDocumentRequestModal({
  plotId,
  onClose,
}: {
  plotId: string
  onClose: () => void
}) {
  const createRequest = useCreateDocumentRequest()
  const [title, setTitle] = useState('')
  const [instructions, setInstructions] = useState('')
  const [items, setItems] = useState<string[]>([''])
  const [dueDate, setDueDate] = useState('')
  const [error, setError] = useState<string | null>(null)

  function updateItem(index: number, value: string) {
    setItems((prev) => prev.map((item, i) => (i === index ? value : item)))
  }

  function addItem() {
    setItems((prev) => [...prev, ''])
  }

  function removeItem(index: number) {
    setItems((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    const labels = items.map((i) => i.trim()).filter(Boolean)
    if (labels.length === 0) {
      setError('Add at least one document to request')
      return
    }

    try {
      await createRequest.mutateAsync({
        plotId,
        title: title.trim(),
        instructions: instructions.trim(),
        items: labels.map((label) => ({ label })),
        ...(dueDate && { dueDate: new Date(dueDate).toISOString() }),
      })
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not create document request'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="create-document-request-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <h2 id="create-document-request-title" className="font-serif text-xl text-ink">
            Request documents
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={(e) => void handleSubmit(e)}>
        <ModalBody className="space-y-4">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Boundary wall construction approval"
            required
            minLength={3}
          />

          <div className="space-y-1.5">
            <label
              htmlFor="document-request-instructions"
              className="font-sans text-sm font-medium text-ink"
            >
              Instructions (optional)
            </label>
            <textarea
              id="document-request-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              placeholder="Any context the member needs — e.g. required format, who to consult"
              className="flex w-full rounded border border-input bg-card px-3.5 py-2 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
            />
          </div>

          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink">Documents needed</label>
            <div className="space-y-2">
              {items.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={item}
                    onChange={(e) => updateItem(index, e.target.value)}
                    placeholder="e.g. Architectural drawings"
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                    className="rounded p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Remove item"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-1.5 font-sans text-sm font-medium text-primary hover:underline"
            >
              <Plus size={15} />
              Add another document
            </button>
          </div>

          <Input
            type="date"
            label="Due date (optional)"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />

          {error && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={createRequest.isPending}>
            Send request
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
